import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { catchError, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';

import { BypassAPIGlobalHandleEnums, LOG_TYPE } from '../constants';
import { ApiService } from './api.service';
import { CustomCookieServiceService } from './custom-cookie-service.service';
import { LogService } from './log.service';
import { UtilityService } from './utility.service';

const PARTNER_SOURCE_COOKIE = 'partner_source';
const PARTNER_SOURCE_TTL_DAYS = 30;
/** Heard-about option texts (lower-case) that mean "through a partner"; the option is managed in tutoring-admin. */
const PARTNER_HEARD_ABOUT_OPTION_TEXTS = ['een bijlespartner', 'ein nachhilfepartner'];

export interface Partner {
  id?: string;
  name: string;
  country: string;
  utmSource: string;
  logoUrl: string;
}

export interface PartnerSource {
  source: string;
  medium: string;
  campaign: string;
  visitedAt: string;
}

export interface HeardAboutOption {
  id: string | number;
  text: string;
}

export interface PartnerRegistrationFields {
  partnerUtmSource?: string;
  partnerUtmMedium?: string;
}

@Injectable({ providedIn: 'root' })
export class PartnerAttributionService {
  private partners$: Observable<Partner[]> | null = null;
  /** The capture of the URL the app was opened with, shared by every consumer of `getPartner$`. */
  private currentVisit$: Observable<Partner | null> | null = null;

  constructor(
    private apiService: ApiService,
    private cookieService: CustomCookieServiceService,
    private utilityService: UtilityService,
    private logService: LogService,
  ) { }

  /** App initializer: stores the partner of the current URL, if any. Never blocks bootstrap; captured once per session. */
  captureFromCurrentUrl(): void {
    if (!this.utilityService.isBrowser()) {
      return;
    }
    this.currentVisit$ = this.capture$(new URLSearchParams(window.location.search)).pipe(shareReplay(1));
    this.currentVisit$.subscribe();
  }

  /**
   * Stores a partner visit when `utm_source` names a partner of this country, and emits that partner.
   * A visit from anything else (own ads, unknown sources) emits null and leaves an earlier partner cookie alone.
   */
  capture$(params: URLSearchParams | Params): Observable<Partner | null> {
    const read = (key: string): string =>
      normalize(params instanceof URLSearchParams ? params.get(key) : params[key]);
    const source = read('utm_source');
    if (!source) {
      return of(null);
    }
    return this.loadPartners().pipe(
      map((partners) => findPartner(partners, source)),
      tap((partner) => {
        if (!partner) {
          return;
        }
        const partnerSource: PartnerSource = {
          source,
          medium: read('utm_medium'),
          campaign: read('utm_campaign'),
          visitedAt: new Date().toISOString(),
        };
        this.cookieService.put(PARTNER_SOURCE_COOKIE, JSON.stringify(partnerSource), {
          expires: PARTNER_SOURCE_TTL_DAYS,
          path: '/',
          sameSite: 'Lax',
        });
      }),
    );
  }

  /** The stored partner visit, or null when there is none or it is older than 30 days. Synchronous. */
  getStoredSource(): PartnerSource | null {
    const raw = this.cookieService.get(PARTNER_SOURCE_COOKIE);
    if (!raw) {
      return null;
    }
    try {
      const stored = JSON.parse(raw) as PartnerSource;
      if (!stored?.source) {
        return null;
      }
      const ageMs = Date.now() - new Date(stored.visitedAt).getTime();
      if (!(ageMs >= 0 && ageMs <= PARTNER_SOURCE_TTL_DAYS * 864e5)) {
        return null;
      }
      return stored;
    } catch {
      return null;
    }
  }

  hasValidSource(): boolean {
    return this.getStoredSource() !== null;
  }

  /**
   * The partner behind the stored visit, or null (no visit, expired, or the partner no longer exists).
   * It first waits for the current visit's capture (done once, at bootstrap): on a partner's landing page the
   * cookie is written only once the partner list has arrived, and a consumer (badge, registration step) may
   * initialise before that.
   */
  getPartner$(): Observable<Partner | null> {
    if (!this.utilityService.isBrowser()) {
      return of(null);
    }
    return (this.currentVisit$ ?? of(null)).pipe(
      switchMap(() => {
        const stored = this.getStoredSource();
        if (!stored) {
          return of(null);
        }
        return this.loadPartners().pipe(
          map((partners) => findPartner(partners, stored.source)),
          tap((partner) => {
            if (!partner) {
              this.logService.report(
                LOG_TYPE.WARNING,
                `Partner attribution: stored source "${stored.source}" is not a partner of ${this.utilityService.country}`,
              );
            }
          }),
        );
      }),
    );
  }

  /** Spread into the `user` object of `POST user/new`. Empty for a regular visitor. */
  registrationFields(): PartnerRegistrationFields {
    const stored = this.getStoredSource();
    if (!stored) {
      return {};
    }
    return stored.medium
      ? { partnerUtmSource: stored.source, partnerUtmMedium: stored.medium }
      : { partnerUtmSource: stored.source };
  }

  /**
   * The generic "came through a partner" heard-about option, to preselect for a partner visitor so the
   * registration does not land under "Anders" in the marketing reports (Koen, 2026-10-26). Null for a
   * regular visitor, or when the list has no such option (then a warning is logged and the answer stays empty).
   */
  partnerHeardAboutOption(options: HeardAboutOption[]): HeardAboutOption | null {
    const stored = this.getStoredSource();
    if (!stored) {
      return null;
    }
    const option = options.find((candidate) => PARTNER_HEARD_ABOUT_OPTION_TEXTS.includes(normalize(candidate.text))) ?? null;
    if (!option) {
      this.logService.report(
        LOG_TYPE.WARNING,
        `Partner attribution: no "${PARTNER_HEARD_ABOUT_OPTION_TEXTS[0]}" heard-about option for ${stored.source} (${this.utilityService.country})`,
      );
    }
    return option;
  }

  /** Partners of the current country, fetched once per app instance. A failed request is retried on the next call. */
  loadPartners(): Observable<Partner[]> {
    if (!this.partners$) {
      const country = normalize(this.utilityService.country);
      // Bypass the global error handling: a failure here must reach `catchError` (so the cache resets and
      // the next call retries) instead of being swallowed with a toast for a request the visitor never made.
      const headers = this.utilityService.addHeaders(BypassAPIGlobalHandleEnums.All);
      this.partners$ = this.apiService.getWithHeaders(`user/partners?country=${country}`, headers).pipe(
        map((response) => toPartners(response).filter((partner) => !partner.country || partner.country === country)),
        catchError((error) => {
          this.partners$ = null;
          this.logService.report(
            LOG_TYPE.ERROR,
            `Partner attribution: could not load partners (${error?.status ?? 'no status'})`,
          );
          return of([] as Partner[]);
        }),
        shareReplay(1),
      );
    }
    return this.partners$;
  }
}

function findPartner(partners: Partner[], source: string): Partner | null {
  return partners.find((partner) => partner.utmSource === source) ?? null;
}

function toPartners(response: unknown): Partner[] {
  const list = Array.isArray(response)
    ? response
    : ((response as { partners?: unknown[] })?.partners ?? []);
  return list
    .map((raw) => {
      const item = raw as Record<string, unknown>;
      return {
        id: String(item['id'] ?? ''),
        name: String(item['name'] ?? ''),
        country: normalize(item['country'] as string),
        utmSource: normalize((item['utmSource'] ?? item['utm_source']) as string),
        logoUrl: String(item['logoUrl'] ?? item['logo_url'] ?? ''),
      };
    })
    .filter((partner) => !!partner.utmSource);
}

function normalize(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}
